import { UserRoles } from "../../types/userRoles";
import { AuthService } from "../auth/auth.service";
import { LocalStorageService } from "../local-storage/local-storage";
import { history } from "react-router-guard";

/**
 * Type definition for user object stored in localStorage
 */
interface StoredUser {
  role?: string;
  [key: string]: any;
}

/**
 * Route paths constants
 */
const ROUTE_PATHS = {
  LOGIN: "/login",
  WORKSPACES: "/workspaces",
  DASHBOARD: "/dashboard",
  USERS: "/users",
} as const;

/**
 * GuardsService handles route protection and authorization checks
 */
export class GuardsService {
  private authService: AuthService = new AuthService();
  private localStorageService: LocalStorageService = new LocalStorageService();

  /**
   * Helper method to get user from localStorage
   */
  private getUser(): StoredUser | null {
    try {
      return this.localStorageService.getData("user");
    } catch {
      return null;
    }
  }

  /**
   * Helper method to get token from localStorage
   */
  private getToken(): string | null {
    try {
      return this.localStorageService.getData("token");
    } catch {
      return null;
    }
  }

  /**
   * Helper method to get tenantId from localStorage
   */
  private getTenantId(): string | null {
    try {
      return this.localStorageService.getData("tenantId");
    } catch {
      return null;
    }
  }

  /**
   * Helper method to redirect to a path
   */
  private redirect(path: string): void {
    history.push({ pathname: path });
  }

  /**
   * Helper method to check if user has one of the specified roles
   */
  private hasRole(user: StoredUser | null, roles: string[]): boolean {
    return user?.role ? roles.includes(user.role) : false;
  }

  /**
   * Helper method to create a guard promise with role check
   */
  private createRoleGuard(
    allowedRoles: string[],
    redirectPath: string
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const user = this.getUser();
        if (this.hasRole(user, allowedRoles)) {
          return resolve(true);
        } else {
          this.redirect(redirectPath);
          return reject();
        }
      } catch (error) {
        this.redirect(redirectPath);
        return reject();
      }
    });
  }

  /**
   * Checks if user is authenticated (has token and optionally tenantId)
   * Redirects to workspaces if token exists but no tenantId
   * Redirects to login if no token
   */
  public isAuthorized = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        const token = this.getToken();
        const tenantId = this.getTenantId();

        if (!token) {
          this.redirect(ROUTE_PATHS.LOGIN);
          return reject();
        }

        if (!tenantId) {
          this.redirect(ROUTE_PATHS.WORKSPACES);
          return resolve(token);
        }

        return resolve(token);
      } catch (error) {
        this.redirect(ROUTE_PATHS.LOGIN);
        return reject();
      }
    });
  };

  /**
   * Checks if user is NOT authenticated
   * Redirects to workspaces if user is already authenticated
   */
  public isNotAuthorized = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      try {
        const token = this.getToken();

        if (token) {
          this.redirect(ROUTE_PATHS.WORKSPACES);
          return reject(token);
        }

        return resolve(true);
      } catch {
        return resolve(true);
      }
    });
  };

  /**
   * Checks if user has SUPER_ADMIN role
   * Redirects to dashboard if unauthorized
   */
  public isSuperAdmin = (): Promise<boolean> => {
    return this.createRoleGuard([UserRoles.SUPER_ADMIN], ROUTE_PATHS.DASHBOARD);
  };

  /**
   * Checks if user has SUPER_ADMIN or COORDINATOR role
   * Redirects to dashboard if unauthorized
   */
  public isSuperUser = (): Promise<boolean> => {
    return this.createRoleGuard(
      [UserRoles.SUPER_ADMIN, UserRoles.COORDINATOR],
      ROUTE_PATHS.DASHBOARD
    );
  };

  /**
   * Checks if user has case admin permissions
   * Allowed roles: EDITOR, AUDITOR, CONTRIBUTOR, SUPER_ADMIN, COORDINATOR
   * Redirects to users page if unauthorized
   */
  public isCaseAdmin = (): Promise<boolean> => {
    return this.createRoleGuard(
      [
        UserRoles.SUPER_ADMIN,
        UserRoles.COORDINATOR,
        UserRoles.EDITOR,
        UserRoles.AUDITOR,
        UserRoles.CONTRIBUTOR,
      ],
      ROUTE_PATHS.DASHBOARD
    );
  };

  /**
   * Checks if user is authorized to create a case
   * Allowed roles: SUPER_ADMIN, COORDINATOR, EDITOR
   * CONTRIBUTOR is allowed only if query parameter includes "USER"
   * Redirects to dashboard if unauthorized
   */
  public isCreateCaseAuthorized = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      try {
        const user = this.getUser();
        const queryParam = history.location.search;

        if (!user) {
          this.redirect(ROUTE_PATHS.DASHBOARD);
          return reject();
        }

        const userRole = user.role;

        // Check for full access roles
        if (
          userRole === UserRoles.SUPER_ADMIN ||
          userRole === UserRoles.COORDINATOR ||
          userRole === UserRoles.EDITOR
        ) {
          return resolve(true);
        }

        // CONTRIBUTOR is allowed only with specific query parameter
        if (userRole === UserRoles.CONTRIBUTOR && queryParam.includes("USER")) {
          return resolve(true);
        }

        // Unauthorized
        this.redirect(ROUTE_PATHS.DASHBOARD);
        return reject();
      } catch (error) {
        this.redirect(ROUTE_PATHS.DASHBOARD);
        return reject();
      }
    });
  };
}
